// ASTRA Refining + Crafting recipes (Fase 1).
//
// Recipes are STATIC, versioned design data — the single source of truth shared
// by the server (validation + craft_atomic input resolution) and the client
// (recipe preview / shop UI). There is no recipe table in the DB.
//
// Provenance of the numbers (locked & human-reviewed — see docs/refining_crafting.md §7):
//   - Refining: fixed 4 ore -> 1 alloy, same tier (NOT derived from the % rule).
//   - Crafting: material cost target = 55% of the output's `value`, split
//     bulk(alloy) 60% / specialty 40% (primary 25% + secondary 15% when two),
//     converted to integer quantities via each input's `value`, rounded to
//     "nice" numbers. HARD tier gating: every input is the SAME tier as the output.
// The table below is the approved, rounded result; treat it as authoritative
// data, not something to recompute at runtime.

import { RESOURCES } from './constants.js';
import { NEW_RESOURCES } from './resources.js';
import { ALL_EQUIPMENT } from './equipment.js';

export type RecipeTier = 1 | 2 | 3 | 4;
export type RecipeKind = 'refine' | 'craft';
// Maps 1:1 to the shops seeded on a station (1 Refinery + 4 Crafting shops).
export type ShopCategory = 'ore' | 'weapons' | 'tools' | 'enhancers' | 'utilities';

export interface RecipeIngredient {
  itemType: 'resource';
  itemId: string;       // resource key, e.g. 'iron', 'steel_alloy', 'hydrogen'
  quantity: number;
}

export interface RecipeOutput {
  itemType: 'resource' | 'equipment';
  itemId: string;       // 'steel_alloy' (refine) | 'weapon_kinetic_t1' (craft)
  quantity: number;
}

export interface Recipe {
  id: string;           // 'refine_steel_alloy' | the output equipment id for crafts
  kind: RecipeKind;
  shopCategory: ShopCategory;
  tier: RecipeTier;
  output: RecipeOutput;
  inputs: RecipeIngredient[];
}

// ==================== REFINING (4 ore -> 1 alloy, same tier) ====================
// [outputResourceId, tier, oreInputId]
const REFINE_SPECS: [string, RecipeTier, string][] = [
  ['steel_alloy',   1, 'iron'],
  ['cryo_bronze',   2, 'copper'],
  ['carbide_plate', 3, 'titanium'],
  ['quantum_alloy', 4, 'platinum'],
];

// ==================== CRAFTING (52 recipes) ====================
// [outputEquipmentId, tier, shopCategory, [[resourceId, qty], ...]]
const CRAFT_SPECS: [string, RecipeTier, ShopCategory, [string, number][]][] = [
  // --- WEAPONS: kinetic (alloy + gas) ---
  ['weapon_kinetic_t1', 1, 'weapons', [['steel_alloy', 3], ['hydrogen', 10]]],
  ['weapon_kinetic_t2', 2, 'weapons', [['cryo_bronze', 5], ['nitrogen', 15]]],
  ['weapon_kinetic_t3', 3, 'weapons', [['carbide_plate', 9], ['xenon', 25]]],
  ['weapon_kinetic_t4', 4, 'weapons', [['quantum_alloy', 9], ['antimatter', 20]]],
  // --- WEAPONS: energy (alloy + crystal + gas) ---
  ['weapon_energy_t1', 1, 'weapons', [['steel_alloy', 4], ['quartz', 3], ['hydrogen', 4]]],
  ['weapon_energy_t2', 2, 'weapons', [['cryo_bronze', 6], ['silicates', 5], ['nitrogen', 7]]],
  ['weapon_energy_t3', 3, 'weapons', [['carbide_plate', 9], ['rare_crystals', 7], ['xenon', 10]]],
  ['weapon_energy_t4', 4, 'weapons', [['quantum_alloy', 10], ['singularity_shards', 7], ['antimatter', 9]]],
  // --- WEAPONS: explosive (alloy + organic + gas) ---
  ['weapon_explosive_t1', 1, 'weapons', [['steel_alloy', 5], ['biomass', 5], ['hydrogen', 5]]],
  ['weapon_explosive_t2', 2, 'weapons', [['cryo_bronze', 8], ['polymers', 7], ['nitrogen', 8]]],
  ['weapon_explosive_t3', 3, 'weapons', [['carbide_plate', 11], ['nanocarbon', 10], ['xenon', 12]]],
  ['weapon_explosive_t4', 4, 'weapons', [['quantum_alloy', 12], ['exotic_compounds', 9], ['antimatter', 11]]],

  // --- TOOLS: mining (alloy + crystal + gas) ---
  ['tool_mining_t1', 1, 'tools', [['steel_alloy', 3], ['quartz', 2], ['hydrogen', 3]]],
  ['tool_mining_t2', 2, 'tools', [['cryo_bronze', 5], ['silicates', 4], ['nitrogen', 5]]],
  ['tool_mining_t3', 3, 'tools', [['carbide_plate', 7], ['rare_crystals', 5], ['xenon', 8]]],
  ['tool_mining_t4', 4, 'tools', [['quantum_alloy', 8], ['singularity_shards', 6], ['antimatter', 7]]],

  // --- ENHANCERS: hull plating (alloy + organic) ---
  ['enhancer_hull_t1', 1, 'enhancers', [['steel_alloy', 4], ['biomass', 6]]],
  ['enhancer_hull_t2', 2, 'enhancers', [['cryo_bronze', 6], ['polymers', 9]]],
  ['enhancer_hull_t3', 3, 'enhancers', [['carbide_plate', 9], ['nanocarbon', 12]]],
  ['enhancer_hull_t4', 4, 'enhancers', [['quantum_alloy', 10], ['exotic_compounds', 12]]],
  // --- ENHANCERS: cargo expander (alloy + organic) ---
  ['enhancer_cargo_t1', 1, 'enhancers', [['steel_alloy', 3], ['biomass', 4]]],
  ['enhancer_cargo_t2', 2, 'enhancers', [['cryo_bronze', 5], ['polymers', 7]]],
  ['enhancer_cargo_t3', 3, 'enhancers', [['carbide_plate', 7], ['nanocarbon', 9]]],
  ['enhancer_cargo_t4', 4, 'enhancers', [['quantum_alloy', 8], ['exotic_compounds', 9]]],
  // --- ENHANCERS: sensor array (alloy + crystal) ---
  ['enhancer_sensor_t1', 1, 'enhancers', [['steel_alloy', 3], ['quartz', 4]]],
  ['enhancer_sensor_t2', 2, 'enhancers', [['cryo_bronze', 5], ['silicates', 7]]],
  ['enhancer_sensor_t3', 3, 'enhancers', [['carbide_plate', 7], ['rare_crystals', 9]]],
  ['enhancer_sensor_t4', 4, 'enhancers', [['quantum_alloy', 8], ['singularity_shards', 9]]],
  // --- ENHANCERS: thruster booster (alloy + gas + ice) ---
  ['enhancer_booster_t1', 1, 'enhancers', [['steel_alloy', 4], ['hydrogen', 7], ['water_ice', 3]]],
  ['enhancer_booster_t2', 2, 'enhancers', [['cryo_bronze', 6], ['nitrogen', 11], ['ammonia', 4]]],
  ['enhancer_booster_t3', 3, 'enhancers', [['carbide_plate', 9], ['xenon', 15], ['methane', 7]]],
  ['enhancer_booster_t4', 4, 'enhancers', [['quantum_alloy', 10], ['antimatter', 15], ['helium3', 7]]],
  // --- ENHANCERS: heat dissipator (alloy + ice) ---
  ['enhancer_heat_t1', 1, 'enhancers', [['steel_alloy', 4], ['water_ice', 8]]],
  ['enhancer_heat_t2', 2, 'enhancers', [['cryo_bronze', 7], ['ammonia', 13]]],
  ['enhancer_heat_t3', 3, 'enhancers', [['carbide_plate', 10], ['methane', 20]]],
  ['enhancer_heat_t4', 4, 'enhancers', [['quantum_alloy', 11], ['helium3', 20]]],
  // --- ENHANCERS: shield amplifier (alloy + gas + crystal) ---
  ['enhancer_shield_amp_t1', 1, 'enhancers', [['steel_alloy', 11], ['hydrogen', 20], ['quartz', 6]]],
  ['enhancer_shield_amp_t2', 2, 'enhancers', [['cryo_bronze', 19], ['nitrogen', 35], ['silicates', 9]]],
  ['enhancer_shield_amp_t3', 3, 'enhancers', [['carbide_plate', 25], ['xenon', 45], ['rare_crystals', 11]]],
  ['enhancer_shield_amp_t4', 4, 'enhancers', [['quantum_alloy', 30], ['antimatter', 40], ['singularity_shards', 11]]],

  // --- UTILITIES: beacon deployer (alloy + crystal + gas) ---
  ['utility_beacon_t1', 1, 'utilities', [['steel_alloy', 8], ['quartz', 6], ['hydrogen', 8]]],
  ['utility_beacon_t2', 2, 'utilities', [['cryo_bronze', 12], ['silicates', 10], ['nitrogen', 13]]],
  ['utility_beacon_t3', 3, 'utilities', [['carbide_plate', 17], ['rare_crystals', 13], ['xenon', 18]]],
  ['utility_beacon_t4', 4, 'utilities', [['quantum_alloy', 20], ['singularity_shards', 13], ['antimatter', 17]]],
  // --- UTILITIES: fighter drone bay (alloy + organic + gas) ---
  ['utility_fighter_bay_t1', 1, 'utilities', [['steel_alloy', 75], ['biomass', 75], ['hydrogen', 80]]],
  ['utility_fighter_bay_t2', 2, 'utilities', [['cryo_bronze', 95], ['polymers', 85], ['nitrogen', 100]]],
  ['utility_fighter_bay_t3', 3, 'utilities', [['carbide_plate', 85], ['nanocarbon', 75], ['xenon', 90]]],
  ['utility_fighter_bay_t4', 4, 'utilities', [['quantum_alloy', 75], ['exotic_compounds', 55], ['antimatter', 65]]],
  // --- UTILITIES: miner drone bay (alloy + organic + crystal) ---
  ['utility_miner_bay_t1', 1, 'utilities', [['steel_alloy', 55], ['biomass', 55], ['quartz', 30]]],
  ['utility_miner_bay_t2', 2, 'utilities', [['cryo_bronze', 70], ['polymers', 60], ['silicates', 35]]],
  ['utility_miner_bay_t3', 3, 'utilities', [['carbide_plate', 65], ['nanocarbon', 55], ['rare_crystals', 30]]],
  ['utility_miner_bay_t4', 4, 'utilities', [['quantum_alloy', 55], ['exotic_compounds', 40], ['singularity_shards', 20]]],
];

function buildRecipes(): Record<string, Recipe> {
  const out: Record<string, Recipe> = {};
  for (const [alloyId, tier, oreId] of REFINE_SPECS) {
    const id = `refine_${alloyId}`;
    out[id] = {
      id, kind: 'refine', shopCategory: 'ore', tier,
      output: { itemType: 'resource', itemId: alloyId, quantity: 1 },
      inputs: [{ itemType: 'resource', itemId: oreId, quantity: 4 }],
    };
  }
  for (const [outId, tier, shop, ins] of CRAFT_SPECS) {
    out[outId] = {
      id: outId, kind: 'craft', shopCategory: shop, tier,
      output: { itemType: 'equipment', itemId: outId, quantity: 1 },
      inputs: ins.map(([itemId, quantity]) => ({ itemType: 'resource' as const, itemId, quantity })),
    };
  }
  return out;
}

export const RECIPES: Record<string, Recipe> = buildRecipes();

// ==================== HELPERS ====================

export function getRecipe(id: string): Recipe | undefined {
  return RECIPES[id];
}

export function getAllRecipes(): Recipe[] {
  return Object.values(RECIPES);
}

export function getRecipesByShop(shopCategory: ShopCategory): Recipe[] {
  return Object.values(RECIPES).filter(r => r.shopCategory === shopCategory);
}

/** Market/base value of one unit of a resource (ore from RESOURCES, rest from NEW_RESOURCES). */
export function getResourceValue(itemId: string): number {
  const ore = (RESOURCES as Record<string, { value: number }>)[itemId];
  if (ore) return ore.value;
  const res = (NEW_RESOURCES as Record<string, { value: number }>)[itemId];
  if (res) return res.value;
  return 0;
}

/** Total input value for `runs` crafts of a recipe — the base the shop fee is charged on. */
export function getRecipeInputValue(recipe: Recipe, runs: number = 1): number {
  let total = 0;
  for (const ing of recipe.inputs) total += getResourceValue(ing.itemId) * ing.quantity;
  return total * runs;
}

/**
 * Dev/test self-check: every output id resolves, every input is a known resource,
 * and HARD tier gating holds (all inputs share the output's tier). Throws on the
 * first violation. Not called at import — invoke from a test/script.
 */
export function validateRecipes(): void {
  const tierOf = (id: string): number | undefined => {
    const ore = (RESOURCES as Record<string, unknown>)[id] ? undefined : undefined; // ore has no tier field
    void ore;
    const res = (NEW_RESOURCES as Record<string, { tier: number }>)[id];
    return res ? res.tier : undefined;
  };
  // Ore tier comes from RESOURCE_TIERS-equivalent ordering; refine inputs are ore,
  // whose tier equals the recipe tier by construction, so we only gate craft inputs
  // that are NEW_RESOURCES (alloys + specialties all live there).
  for (const r of Object.values(RECIPES)) {
    if (r.kind === 'craft') {
      if (!ALL_EQUIPMENT[r.output.itemId]) {
        throw new Error(`Recipe ${r.id}: output equipment '${r.output.itemId}' not found`);
      }
      if (ALL_EQUIPMENT[r.output.itemId].tier !== r.tier) {
        throw new Error(`Recipe ${r.id}: output tier ${ALL_EQUIPMENT[r.output.itemId].tier} != recipe tier ${r.tier}`);
      }
    }
    for (const ing of r.inputs) {
      if (getResourceValue(ing.itemId) <= 0) {
        throw new Error(`Recipe ${r.id}: input '${ing.itemId}' is not a known resource`);
      }
      // Tier gating: NEW_RESOURCES inputs (alloys + specialties) must match recipe tier.
      const t = tierOf(ing.itemId);
      if (t !== undefined && t !== r.tier) {
        throw new Error(`Recipe ${r.id} (T${r.tier}): input '${ing.itemId}' is T${t} (tier gating violation)`);
      }
    }
  }
}
